import { Button, Checkbox, FormControlLabel } from '@material-ui/core';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { PublishGate } from 'services/health';
import { UseCoursePublishResult } from '../hooks/useCoursePublish';
import PublishProgressBar from './PublishProgressBar';
import PublishRef from './PublishRef';

type IProps = {
  coursePublish: UseCoursePublishResult;
  gate: PublishGate | null;
  gateLoading: boolean;
};

// The publish action of the (single) Publish module: lives under the course's gate panel.
// green/warning publish directly; red or a course never verified needs the explicit
// override checkbox (decision Raúl, 18 aug 2026).
const CoursePublish: React.FC<IProps> = ({ coursePublish, gate, gateLoading }) => {
  const { model, summary, publish } = coursePublish;
  const [override, setOverride] = useState(false);

  const gateStatus = gate?.status ?? 'no-run';
  const gateAllows = gateStatus === 'green' || gateStatus === 'warning';
  const needsOverride = !gateLoading && !gateAllows;
  const publishEnabled = !gateLoading && !model.publishing && model.resources.length > 0 && (gateAllows || override);

  const gateResourceByRef = useMemo(() => {
    const map = new Map<number, NonNullable<typeof gate>['resources'][number]>();
    gate?.resources.forEach((resource) => map.set(resource.ref, resource));
    return map;
  }, [gate]);

  const showRun = summary.state !== 'idle';

  return (
    <PublishContainer>
      <ActionRow>
        <Button
          disabled={!publishEnabled}
          variant="contained"
          color={gateAllows ? 'primary' : 'secondary'}
          size="small"
          onClick={() => void publish()}
        >
          publish
        </Button>
        {needsOverride && (
          <FormControlLabel
            control={
              <Checkbox checked={override} onChange={(event) => setOverride(event.target.checked)} size="small" />
            }
            label={
              <OverrideLabel>
                {gateStatus === 'no-run'
                  ? 'Publish without a stored verification run'
                  : 'Publish despite blocking issues (gate red)'}
              </OverrideLabel>
            }
          />
        )}
        {showRun && (
          <RunProgress>
            <RunBar
              percent={summary.percent}
              tone={summary.state === 'failed' ? 'error' : summary.state === 'done' ? 'success' : 'active'}
            />
            <RunText>
              {summary.state === 'publishing'
                ? `${summary.doneResources}/${summary.totalResources} resources`
                : summary.state === 'done'
                ? `Published · ${summary.totalResources} resources`
                : `${summary.failedResources} failed`}
            </RunText>
          </RunProgress>
        )}
      </ActionRow>
      {showRun && (
        <ResourcesContainer>
          {model.resources.map((resource) => (
            <PublishRef key={resource.ref} model={resource} gateResource={gateResourceByRef.get(resource.ref)} />
          ))}
        </ResourcesContainer>
      )}
    </PublishContainer>
  );
};

export default CoursePublish;

const PublishContainer = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background-color: #fff;
  padding: 10px 14px;
  margin-bottom: 12px;
  width: 100%;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const OverrideLabel = styled.span`
  font-size: 13px;
  color: #b91c1c;
`;

const RunProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  max-width: 320px;
  margin-left: auto;
`;

const RunBar = styled(PublishProgressBar)`
  flex: 1 1 auto;
`;

const RunText = styled.span`
  font-size: 12px;
  color: #4b5563;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const ResourcesContainer = styled.div`
  margin-top: 10px;
  border-top: 1px solid #f3f4f6;
  padding-top: 8px;
`;
